import { getUserRaw } from "./getUserInformation";

/**
 * Enum-like object of all known permission names.
 * Values MUST match server Permission.Name.
 * @readonly
 * @enum {string}
 */
export const PermissionType = Object.freeze({
  ManageUcSkillsAccess: "ManageUcSkillsAccess",
  ManageResellers: "ManageResellers",
  ManageAdminUsers: "ManageAdminUsers",
  ViewReports: "ViewReports",
  SetWhiteLabelling: "SetWhiteLabelling",
  AccessInvoices: "AccessInvoices",
});

/** @typedef {{ Name: string, Description: string }} Permission */

/**
 * Build a Map keyed by UPPERCASE Name, deduped (first one wins).
 * @returns {Map<string, Permission>}
 */
function getPermissionMap() {
  const user = getUserRaw();
  const list = Array.isArray(user?.Permissions) ? user.Permissions : [];
  const map = new Map();

  for (const raw of list) {
    if (!raw || typeof raw !== "object") {
      continue; 
    }
    const name = (raw.Name ?? raw.name ?? "").toString().trim();
    const displayName = (raw.DisplayName ?? raw.displayName ?? "").toString().trim();
    const description = (raw.Description ?? raw.description ?? "").toString().trim();
    if (!name) {
      continue;
    }
    const key = name.toUpperCase();
    if (!map.has(key)) {
      map.set(key, { Name: name, DisplayName: displayName, Description: description });
    }
  }
  return map;
}

/**
 * Public: full, deduped list of permissions {Name, DisplayName, Description}.
 * @returns {Permission[]}
 */
export function getPermissions() {
  return Array.from(getPermissionMap().values());
}

/**
 * Public: description for a permission by name (case-insensitive); "" if none.
 * @param {string} name
 * @returns {string}
 */
export function getPermissionDescription(name) {
  if (!name) {
    return "";
  }
  const p = getPermissionMap().get(String(name).toUpperCase());
  return p?.Description ?? "";
}

/**
 * Internal: Set of upper-cased permission names for fast checks.
 * @returns {Set<string>}
 */
function getPermissionNameSet() {
  return new Set(getPermissionMap().keys());
}

/**
 * @param {string} p  // PermissionType value (permission Name)
 * @returns {boolean}
 */
export function hasPermission(p) {
  if (!p) {
    return false;
  }
  return getPermissionNameSet().has(String(p).toUpperCase());
}

/**
 * True if user has ANY of the provided permission names.
 * @param {...string} perms  // PermissionType[]
 * @returns {boolean}
 */
export function hasAny(...perms) {
  const set = getPermissionNameSet();
  for (const p of perms) {
    if (p && set.has(String(p).toUpperCase())) {
      return true;
    }
  }
  return false;
}

/**
 * True if user has ALL of the provided permission names.
 * @param {...string} perms  // PermissionType[]
 * @returns {boolean}
 */
export function hasAll(...perms) {
  const set = getPermissionNameSet();
  for (const p of perms) {
    if (!p || !set.has(String(p).toUpperCase())) {
      return false;
    }
  }
  return true;
}